// src/hooks/useDocumentGeneration.js
// Hook for document generation with support for both local DOCX and Google Drive API

import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { generateDocument } from '../utils/documentGenerator';
import { generateDocumentWithGoogleDrive, checkGoogleDriveStatus } from '../utils/googleDocumentGenerator';
import { getGoogleAuthUrl } from '../api/googleDrive';

/**
 * Hook for document generation with mode selection (local DOCX or Google Drive API)
 *
 * @returns {object} - { generationMode, setGenerationMode, googleStatus, generateDoc, loading, GenerationModeSelector }
 */
export const useDocumentGeneration = () => {
  // Default to Google Drive API mode
  const [generationMode, setGenerationMode] = useState('google');
  const [googleStatus, setGoogleStatus] = useState({ configured: false, authenticated: false });
  const [loading, setLoading] = useState(false);

  // Check Google Drive status on mount
  useEffect(() => {
    const status = checkGoogleDriveStatus();
    setGoogleStatus(status);
    // Always use Google Drive mode
  }, []);

  /**
   * Generate document using selected mode
   *
   * @param {string} type - Document type (Contrat, Confirmation, Facturation)
   * @param {object} data - Data to fill in the template
   * @param {string} charterId - Charter ID
   * @param {string} contractType - Contract type (bateaux, seabob, jetski, lunch)
   * @returns {Promise<object>} - Document record
   */
  const generateDoc = useCallback(async (type, data, charterId, contractType) => {
    setLoading(true);

    try {
      if (generationMode === 'google') {
        console.log('');
        console.log('═══════════════════════════════════════════════');
        console.log('🚀 GENERATING WITH GOOGLE DRIVE API');
        console.log('═══════════════════════════════════════════════');

        try {
          const result = await generateDocumentWithGoogleDrive(type, data, charterId, contractType);

          console.log('✅ Document generated:', result);

          message.success(
            <span>
              Document created! <a href={result.googleDocUrl} target="_blank" rel="noopener noreferrer">Open in Google Docs</a>
            </span>,
            10
          );

          return result;

        } catch (googleError) {
          // Check if it's an authentication error
          if (googleError.message.startsWith('NOT_AUTHENTICATED:')) {
            const authUrl = googleError.message.replace('NOT_AUTHENTICATED:', '');
            message.warning('Please authenticate with Google first');
            window.location.href = authUrl;
            return null;
          }
          throw googleError;
        }

      } else {
        // Use local DOCX generation
        console.log('');
        console.log('═══════════════════════════════════════════════');
        console.log('🚀 GENERATING WITH LOCAL DOCX');
        console.log('═══════════════════════════════════════════════');

        const result = await generateDocument(type, data, charterId, contractType);

        message.success(`${type} generated successfully! File has been downloaded.`, 5);

        return result;
      }

    } catch (error) {
      console.error('❌ Error generating document:', error);
      message.error(`Error: ${error.message}`, 10);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [generationMode]);

  /**
   * Trigger Google authentication
   */
  const authenticateGoogle = useCallback(() => {
    const authUrl = getGoogleAuthUrl();
    window.location.href = authUrl;
  }, []);

  return {
    generationMode,
    setGenerationMode,
    googleStatus,
    generateDoc,
    loading,
    authenticateGoogle,
  };
};

export default useDocumentGeneration;
